import { create } from 'zustand';
import {
  applyTheme,
  readStoredPreference,
  resolveTheme,
  storePreference,
  type ResolvedTheme,
  type ThemePreference,
} from '@/lib/theme';

/**
 * Colour theme preference. UI state only — it holds a display choice and nothing else, so it never
 * touches policy numbers, dates of birth, BF API responses or reCAPTCHA tokens.
 *
 * `preference` is what the user picked (including `system`); `resolved` is what is painted. Keeping
 * both means the toggle can show "System" as a distinct state instead of guessing it back from the
 * colour currently on screen.
 */
interface ThemeState {
  readonly preference: ThemePreference;
  readonly resolved: ResolvedTheme;
  setPreference: (preference: ThemePreference) => void;
  /** Re-resolves against the OS. Only has an effect while the preference is `system`. */
  syncWithSystem: () => void;
}

const initialPreference = readStoredPreference();

export const useThemeStore = create<ThemeState>((set, get) => ({
  preference: initialPreference,
  resolved: resolveTheme(initialPreference),
  setPreference: (preference): void => {
    const resolved = resolveTheme(preference);
    storePreference(preference);
    applyTheme(resolved);
    set({ preference, resolved });
  },
  syncWithSystem: (): void => {
    if (get().preference !== 'system') {
      return;
    }

    const resolved = resolveTheme('system');
    applyTheme(resolved);
    set({ resolved });
  },
}));
